import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import Message from "messaging/message";
import ReadReceipt from "messaging/read-receipts/read-receipt";
import CommentSchema, { CommentJSON, CommentParams } from "work/proposals/comments/_schemas/comment-schema";
import CommentThread from "work/proposals/comments/threads/comment-thread";

type MarkFlags = {
  markedForCreation?: boolean;
  markedForPublish?: boolean;
  markedForEdit?: boolean;
  markedForReadReceipt?: boolean;
  markedForUnread?: boolean;
  markedForDeletion?: boolean;
};

export default class Comment {
  public static readonly EXAMPLE: Comment = new Comment({
    id: Guid.generate(),
    senderId: Guid.generate(),
    thread: CommentThread.EXAMPLE,
    content: "This is a test comment.",
    created: new AHDate(),
    published: new AHDate(),
    issues: [],
    readReceipts: [],
    isAutoGenerated: false,
  });

  public static create(params: CommentParams): Comment {
    const parsed = CommentSchema.validate(params);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid Comment parameters.");
    }
    return new Comment(parsed);
  }

  public static fromJSON(json: CommentJSON): Comment {
    if (isNullOrUndefined(json)) return json;
    const params = CommentSchema.deserialize(json);
    return new Comment(params);
  }

  public readonly id?: Guid;
  public readonly senderId: Guid | null;
  public readonly thread: CommentThread;
  public readonly content: string;
  public readonly created?: AHDate;
  public readonly published?: AHDate;

  public readonly readReceipts: ReadReceipt[] = [];
  public readonly issues: string[] = [];

  public readonly deleted?: AHDate;
  public readonly updated?: AHDate;

  public readonly isAutoGenerated: boolean = false;
  public readonly markedForCreation?: boolean;
  public readonly markedForPublish?: boolean;
  public readonly markedForEdit?: boolean;
  public readonly markedForReadReceipt?: boolean;
  public readonly markedForUnread?: boolean;
  public readonly markedForDeletion?: boolean;

  public constructor(params: CommentParams) {
    const {
      thread,
      content,
      senderId,
      isAutoGenerated,
      id,
      created,
      published,
      issues,
      readReceipts,
      deleted,
      updated,
      markedForCreation,
      markedForPublish,
      markedForEdit,
      markedForReadReceipt,
      markedForUnread,
      markedForDeletion,
    } = params as CommentParams & MarkFlags;

    if (!content) throw new EmptyCommentError();

    this.thread = thread instanceof CommentThread ? thread : new CommentThread(thread);
    this.id = id ?? Guid.generate();
    this.senderId = senderId ?? null;
    this.isAutoGenerated = isAutoGenerated ?? false;
    this.content = content;
    this.created = created;
    this.published = published;

    this.issues = issues ?? [];
    this.readReceipts = readReceipts ?? [];

    this.deleted = deleted;
    this.updated = updated;

    // Flags
    this.markedForCreation = markedForCreation ?? (id ? undefined : true);
    this.markedForPublish = markedForPublish;
    this.markedForEdit = markedForEdit;
    this.markedForReadReceipt = markedForReadReceipt;
    this.markedForUnread = markedForUnread;
    this.markedForDeletion = markedForDeletion;

    Object.freeze(this);
  }

  public toJSON(): CommentJSON {
    return CommentSchema.serialize(this);
  }

  public get isPending(): boolean {
    return Boolean(
      !this.published ||
      this.markedForEdit ||
      this.markedForReadReceipt !== undefined ||
      this.markedForUnread !== undefined ||
      this.markedForDeletion ||
      this.markedForCreation,
    );
  }

  public with(patch: Partial<CommentParams> & MarkFlags): Comment {
    return new Comment({
      ...(this as unknown as CommentParams),
      issues: [...this.issues],
      readReceipts: [...this.readReceipts],
      ...patch,
    });
  }

  public markForCreation(): Comment {
    return this.with({ markedForCreation: true });
  }

  public unmarkForCreation(): Comment {
    return this.with({ markedForCreation: false });
  }

  public markForPublish(): Comment {
    return this.with({ markedForPublish: true });
  }

  public unmarkForPublish(): Comment {
    return this.with({ markedForPublish: false });
  }

  public markForEdit(): Comment {
    return this.with({
      markedForEdit: true,
      updated: new AHDate(),
    });
  }

  public markForDeletion(): Comment {
    return this.with({
      markedForDeletion: true,
      deleted: new AHDate(),
    });
  }

  public unmarkForDeletion(): Comment {
    return this.with({
      markedForDeletion: false,
      deleted: undefined,
    });
  }

  public markForUnread(): Comment {
    if (this.markedForReadReceipt) {
      return this.with({ markedForReadReceipt: false, markedForUnread: false });
    }
    return this.with({ markedForUnread: true });
  }

  public edit(content?: string): Comment {
    return this.with({
      content,
      updated: new AHDate(),
      markedForEdit: this.published ? true : undefined,
    });
  }

  public setDeleted(): Comment {
    return this.with({
      deleted: new AHDate(),
      markedForDeletion: true,
    });
  }

  public setUndeleted(): Comment {
    return this.with({
      deleted: undefined,
      markedForDeletion: false,
    });
  }

  public markForReadReceipt(): Comment {
    if (this.markedForUnread) {
      return this.with({ markedForUnread: false, markedForReadReceipt: false });
    }
    return this.with({ markedForReadReceipt: true });
  }

  public publish(): Comment {
    return this.with({ markedForPublish: true });
  }

  /** Soft delete flag */
  public get isDeleted(): boolean {
    return !!this.deleted;
  }

  public isReadByUser(userId?: Guid): boolean {
    if (!userId) return false;
    return this.readReceipts.some((receipt) => receipt.userId.isEqualTo(userId));
  }

  public static fromMessage(message: Message): Comment {
    return new Comment({
      ...message,
      thread: CommentThread.fromForum(message.forum),
    });
  }

  public toMessage(params: { isViewingAsVendor: boolean }): Message {
    const { isViewingAsVendor } = params;
    const message = Message.create({
      ...this,
      id: this.id ?? Guid.generate(),
      forum: this.thread.toForum({ isViewingAsVendor }),
    });
    if (this.markedForCreation) {
      message.markForCreation();
    }
    if (this.markedForPublish) {
      message.publish();
    }
    if (this.markedForReadReceipt) {
      message.markRead();
    }
    if (this.markedForUnread) {
      message.markUnread();
    }
    if (this.markedForDeletion) {
      message.delete();
    }

    return message;
  }

  public isEqualTo(other: Comment): boolean {
    if (this.id && other.id) {
      return this.id.isEqualTo(other.id);
    }
    return Boolean(
      this.thread.isEqualTo(other.thread) && this.content === other.content && this.senderId?.isEqualTo(other.senderId),
    );
  }
}

export class EmptyCommentError extends Error {
  constructor() {
    super(`Comment cannot be empty`);
  }
}
